import { useState, useMemo, useEffect, useCallback } from 'react'
import { useWireValue } from '@forminator/react-wire'
import * as store from '@store'
import * as actions from '@actions'
import useSortableTableHeaders from '@hook/useSortableTableHeaders'
import { CHECK_REPORT_STATUS_PROCESSING } from '@constants'
import dayjs from 'dayjs'
import { toast } from 'react-toastify'

import CircleSpinner from '@components/shared/CircleSpinner'
import CheckReportsTable from './CheckReportsTable'

import './CheckReports.css'
import ToggleButtons from '@/components/shared/ToggleButtons'
import { FaTrash } from 'react-icons/fa'
import Flatpickr from 'react-flatpickr'
import monthSelect from 'flatpickr/dist/plugins/monthSelect'
import { formatDate } from '@/utils/date'
import 'flatpickr/dist/plugins/monthSelect/style.css'
import { Selectbox } from '@/components/shared/Selectbox'
import { omitFalsey } from '@/utils'
import MappingModal from './MappingModal'
import { PENDO_EVENTS, pendoTrack } from '@/utils/pendo'
import { INTEREST_TYPES_PROD } from '@/constants/checkMappings'
import useRealtimeCheckReportStatus from '@/hook/useRealtimeCheckReportStatus'
import usePollCheckReportStatus from '@/hook/usePollCheckReportStatus'

const headers = [
    ['name', 'Name'],
    ['createdAt', 'Created'],
    ['status', 'Status'],
    ['createdBy', 'Created By'],
    
]

const filterButtons = [
    { slug: 'salesDate', label: 'Production Month', shouldShow: data => data.timePeriod === 'salesDate' },
    { slug: 'incomeDate', label: 'Income Month', shouldShow: data => data.timePeriod === 'incomeDate' },
    { slug: 'operator', label: 'Operator' },
    { slug: 'stateCounty', label: 'State / County' },
    { slug: 'interestType', label: 'Interest Type', shouldShow: data => data.groupBy.includes('interestType') },
    // ['product', 'Product'],
]

const GROUP_BY = [
    { key: 'operator', label: 'Operator' },
    { key: 'organizationId', label: 'Organization Name' },
    { key: 'checkNumber', label: 'Check Number' },
    { key: 'interestType', label: 'Interest Type' },
    
]
const GEOGRAPHY = [
    { key: 'state', label: 'State' },
    { key: 'county', label: 'County' },
]

const OBJECTS = [
    { key: 'property', label: 'Wells' },
]

const TIME_PERIOD = [
    { key: 'salesDate', label: 'Production Month' },
    { key: 'incomeDate', label: 'Income Month' },
]

const PROD_ADJ = [
    { key: 'cdex', label: 'CDEX' },
    { key: 'simple', label: 'Simple' },
    { key: 'source', label: 'Source' },
]

const DEFAULT_DATA = {
    groupBy: ['operator', 'interestType'],
    geography: [],
    objects: 'property',
    timePeriod: 'salesDate',
    product: 'cdex',
    adjustment: 'cdex',
}

const CheckReports = () => {
    
    const [loading, setLoading] = useState(false)
    const [reportName, setReportName] = useState(`Report-${dayjs().format('YYYY-MM-DD')}`)
    const [reports, setReports] = useState([]) // @todo: would be interesting if we have a store for checkReports
    const [formData, setFormData] = useState(DEFAULT_DATA)
    const [filters, setFilters] = useState({})
    const [operators, setOperators] = useState([])
    const [stateCounty, setStateCounty] = useState([])
    const currentOrganization = useWireValue(store.currentOrganization)
    const [showMappingsModal, setShowMappingsModal] = useState(false)
    const [checkMappings, setCheckMappings] = useState([])
    
    const checksReportsInProcessing = useMemo(() => (
        reports?.rows?.filter(it => CHECK_REPORT_STATUS_PROCESSING.includes(it?.status)) ?? []
    ), [reports])
    
    const statesMap = useMemo(() => {
        const mappedData = stateCounty.reduce((acc, curr) => {
            // only return if its a true null, if its blank then leave it as N/A
            if (curr.state === null) return acc
            if (!acc[curr.state]) acc[curr.state || 'N/A'] = []
            
            acc[curr.state || 'N/A'].push(curr.county || 'N/A')
            return acc
        }, {})
        
        Object.values(mappedData).forEach(counties => counties.sort((a, b) => {
            if (a === 'N/A') return 1
            if (b === 'N/A') return -1
            return a.localeCompare(b)
        }))
        return mappedData
    }, [stateCounty])
    
    const countiesMap = useMemo(() => {
        const mappedData = Object.keys(statesMap).reduce((acc, curr) => {
            statesMap[curr].forEach(county => {
                if (!acc[county]) acc[county] = []
                
                acc[county].push(curr)
            })
            return acc
        }, {})
        
        return mappedData
    }, [statesMap])
    
    const stateOptions = useMemo(() => {
        const county = filters.stateCounty?.[1]
        const states = county ? countiesMap[county] : Object.keys(statesMap)
        const opts = states.sort().map(k => ({ value: k, label: k }))
        
        return [{ value: null, label: 'Select a State' }, ...opts]
    }, [filters.stateCounty?.[1], statesMap, countiesMap])
    
    const countyOptions = useMemo(() => {
        const state = filters.stateCounty?.[0]
        const counties = state ? statesMap[state] : Object.keys(countiesMap)
        const opts = counties.sort().map(k => ({ value: k, label: k }))
        
        return [{ value: null, label: 'Select a County' }, ...opts]
    }, [filters.stateCounty?.[0], statesMap, countiesMap])
    
    const {
        orderField,
        orderDirection,
        onHeaderClick,
    } = useSortableTableHeaders(headers)
    
    const setOperatorsAndInterestFilter = (key, value) => {
        const filtersCopy = { ...filters }
        
        filtersCopy[key] = value
        setFilters(filtersCopy)
    }
    
    const setStateCountyFilter = (idx, value) => {
        const filtersCopy = { ...filters }
        
        filtersCopy.stateCounty = filtersCopy.stateCounty || []
        filtersCopy.stateCounty[idx] = value
        setFilters(filtersCopy)
    }
    
    const fetchOperators = async () => {
        
        try {
            
            const res = await actions.fetchAllCheckOperators({ limit: 10000, order: 'ASC' })
            
            setOperators(res.rows)
            
        } catch (e) {
            
            // @todo show error
            console.error('CheckReports#effect.fetchAllCheckOperators', e)
            
        }
        
        setLoading(false)
        
    }
    
    const fetchStateCounty = async () => {
        
        try {
            
            const res = await actions.fetchAllCheckStateCounty()
            
            setStateCounty(res)
            
        } catch (e) {
            // @todo show error
            console.error('CheckReports#effect.fetchStateCounty', e)
        }
        
    }
    
    const setByField = (fieldName, value) => {
        const copy = { ...formData }
        
        copy[fieldName] = value
        setFormData(copy)
    }
    
    const toggleFilter = filterName => {
        const filtersCopy = { ...filters }
        
        if (Object.hasOwn(filtersCopy, filterName)) {
            delete filtersCopy[filterName]
        } else {
            filtersCopy[filterName] = null
        }
        
        setFilters(filtersCopy)
    }
    
    const setDateRangeFilter = (key, dateField, date) => {
        const filtersCopy = { ...filters }
        
        filtersCopy[key] = filtersCopy[key] || {}
        filtersCopy[key][dateField] = formatDate(date, 'YYYY-MM-DD')
        filtersCopy[key][`${dateField}Txt`] = formatDate(date, 'YYYY-MM')
        filtersCopy[key][`${dateField}Obj`] = date
        setFilters(filtersCopy)
    }
    
    const fetchCheckReports = async () => {
        
        setLoading(true)
        
        try {
            
            const res = await actions.fetchCheckReports()
            
            setReports(res || [])
            
        } catch (e) {
            
            console.error('fetchCheckReports', e)
            toast.error('Failed to fetch reports')
            
        }
        
        setLoading(false)
        
    }
    
    const onCreateReportClick = useCallback(async () => {
        
        try {
            const checkReportData = await actions.createCheckReport({
                name: reportName,
                fileName: `${reportName}.xlsx`,
                ...formData,
                filters: omitFalsey(filters),
            })
            
            pendoTrack(PENDO_EVENTS.CHECK_REPORT, {
                checkReportId: checkReportData.id,
                organizationId: currentOrganization.id,
                reportName,
                formData,
                filters: omitFalsey(filters),
            })
            
            setFormData(DEFAULT_DATA)
            setFilters({})
            
            await fetchCheckReports()
            
            toast.success('Report queued for processing')
            
        } catch (e) {
            
            console.error('onCreateReportClick', e)
            toast.error('Failed to create report')
            
        }
        
    }, [currentOrganization.id, filters, formData, reportName])
    
    const onFinishMapping = async editedFields => {
        
        try {
            await actions.saveMappings(Object.values(editedFields))
            await onCreateReportClick()
        } catch (err) {
            toast.error(err.message)
        }
        
        setLoading(false)
    }
    
    const checkAndGenerate = async () => {
        const mappings = await actions.getMappingsFiltered({ ...filters })
        
        if (mappings.length > 0) {
            setShowMappingsModal(true)
            setCheckMappings(mappings)
        } else {
            onCreateReportClick()
        }
    }
    
    const createFilterButton = useCallback((slug, label, shouldShow = () => true) => (
        shouldShow(formData) && !Object.hasOwn(filters, slug) && <button
            className="btn btn-link btn-sm"
            onClick={() => toggleFilter(slug)}>
            {label}
        </button>
    ), [toggleFilter, formData, filters])
    
    const usePollCallback = useCallback((err, ready) => {
        if (err) return console.log('CheckReports', err)
        
        const readyMap = ready.reduce((acc, it) => ({
            ...acc,
            [it.id]: it,
        }), {})
        
        setReports(prev => ({
            count: prev?.count ?? 0,
            rows: (prev?.rows ?? []).map(it => {
                if (!readyMap[it.id]) return it
                return {
                    ...it,
                    ...readyMap[it.id],
                }
            }),
        }))
    }, [setReports])
    
    useRealtimeCheckReportStatus({ ...reports }, setReports)
    usePollCheckReportStatus(checksReportsInProcessing, usePollCallback)
    
    useEffect(() => {
        
        const filtersCopy = { ...filters }
        
        delete filtersCopy.stateCounty
        delete filtersCopy.operator
        setFilters(filtersCopy)
        
        fetchCheckReports()
        fetchStateCounty()
        fetchOperators()
        
    }, [currentOrganization])
    
    useEffect(() => {
        const orgName = currentOrganization.name
        const operator = filters.operator ?? ''
        const state = filters.stateCounty?.[0] ?? ''
        const county = filters.stateCounty?.[1] ?? ''
        const stateCounty = [county, state].filter(v => !!v).join('-')
        const timePeriod = TIME_PERIOD.find(tp => tp.key === formData.timePeriod)?.label ?? ''
        const startDate = filters[formData.timePeriod]?.['startDateTxt'] ?? ''
        const endDate = filters[formData.timePeriod]?.['endDateTxt'] ?? ''
        const dates = [startDate, endDate].filter(v => !!v).join(' to ')
        
        const name = [`[${dayjs().format('YYYY-MM-DD')}] Report`, orgName, operator, stateCounty, timePeriod, dates]
            .filter(v => !!v).join(' - ')
        
        setReportName(name)
    }, [currentOrganization, filters, formData.timePeriod])
    
    useEffect(() => {
        
        if (formData.timePeriod === 'salesDate' && Object.hasOwn(filters, 'incomeDate')) {
            const copy = { ...filters }
            
            delete copy.incomeDate
            setFilters(copy)
        } else if (formData.timePeriod === 'incomeDate' && Object.hasOwn(filters, 'salesDate')) {
            const copy = { ...filters }
            
            delete copy.salesDate
            setFilters(copy)
        }
    }, [formData, filters])
    
    return (
        
        <div className="CheckReports pt-0 p-3">
            
            {/* <h1 className="m-0 h1">Reports</h1> */}
            <fieldset>
                
                <legend>Report Creator</legend>
                
                <div className="fieldset-body">
                    
                    <div>
                        <label>Customize:</label>
                        <div className="flex gap-4 flex-wrap">
                            <ToggleButtons
                                label="Group By"
                                buttons={GROUP_BY}
                                state={formData.groupBy}
                                setter={value => setByField('groupBy', value)}
                                multiple={true} />
                            <ToggleButtons
                                label="Geography"
                                buttons={GEOGRAPHY}
                                state={formData.geography}
                                setter={value => setByField('geography', value)}
                                multiple={true} />
                            <ToggleButtons
                                label="Objects"
                                buttons={OBJECTS}
                                state={formData.objects}
                                setter={value => setByField('objects', value)} />
                            <ToggleButtons
                                label="Time Period"
                                buttons={TIME_PERIOD}
                                state={formData.timePeriod}
                                setter={value => setByField('timePeriod', value)} />
                            <ToggleButtons
                                label="Product"
                                buttons={PROD_ADJ}
                                state={formData.product}
                                setter={value => setByField('product', value)} />
                            <ToggleButtons
                                label="Adjustment"
                                buttons={PROD_ADJ}
                                state={formData.adjustment}
                                setter={value => setByField('adjustment', value)} />
                        </div>
                    </div>
                    
                    <div className="filter-row">
                        <span>Add Filter</span>
                        {filterButtons.map(({ slug, label, shouldShow }) =>
                            createFilterButton(slug, label, shouldShow))}
                    </div>
                    
                    <div className="bordered-row">
                        {Object.keys(filters).map(key => 
                            <div key={key} className="flex items-center gap-3 mb-3 row">
                                <label className="col-1">{filterButtons.find(({ slug }) => slug === key).label}</label>
                                
                                {['incomeDate', 'salesDate'].includes(key) && 
                                    <div className="flex gap-3 items-center col-6">
                                        <div className="form-group flex-1 mb-0">
                                            {/* <label>Starting Date</label> */}
                                            <Flatpickr
                                                className="input input-bordered flatpickr-input"
                                                value={filters[key]?.startDateObj}
                                                name="startDate"
                                                placeholder="Start Date"
                                                options={{
                                                    maxDate: filters[key]?.endDateObj,
                                                    formatDate: date => formatDate(date, 'MMM YY'),
                                                    plugins: [new monthSelect()],
                                                }}
                                                onChange={date => setDateRangeFilter(key, 'startDate', date[0])} />
                                        </div>
                                        <div className="form-group flex-1 mb-0">
                                            <Flatpickr
                                                className="input input-bordered flatpickr-input"
                                                value={filters[key]?.endDateObj}
                                                placeholder="End Date"
                                                name="endDate"
                                                options={{
                                                    minDate: filters[key]?.startDateObj,
                                                    formatDate: date => formatDate(date, 'MMM YY'),
                                                    plugins: [new monthSelect()],
                                                }}
                                                onChange={date => setDateRangeFilter(key, 'endDate', date[0])} />
                                        </div>
                                    </div>
                                }
                                
                                {key === 'operator' &&
                                    <Selectbox className="w-[200px] mb-0"
                                        options={operators?.map(o => ({ value: o.operator, label: o.operator }))}
                                        handleChange={(_, op) => setOperatorsAndInterestFilter('operator', op.value)}
                                        name="operator"
                                        option={filters[key] ? { value: filters[key], label: filters[key] } : null} />}
                                
                                {key === 'stateCounty' &&
                                    <>
                                        <Selectbox className="w-[200px] mb-0"
                                            options={stateOptions}
                                            handleChange={(_, state) => setStateCountyFilter(0, state.value)}
                                            name="state"
                                            option={filters[key]?.[0]
                                                ? { value: filters[key][0], label: filters[key][0] }
                                                : null} />
                                        
                                        <Selectbox className="w-[200px] mb-0"
                                            options={countyOptions}
                                            handleChange={(_, county) => setStateCountyFilter(1, county.value)}
                                            name="county"
                                            option={filters[key]?.[1]
                                                ? { value: filters[key][1], label: filters[key][1] }
                                                : null} />
                                    </>}
                                
                                {key === 'interestType' && 
                                    <Selectbox className="w-[200px] mb-0"
                                        options={INTEREST_TYPES_PROD.map(it => ({ value: it, label: it }))}
                                        handleChange={(_, op) =>
                                            setOperatorsAndInterestFilter('interestType', op.value )}
                                        name="interestType"
                                        option={filters[key] ? { value: filters[key], label: filters[key] } : null} />}
                                
                                <button className="btn h-[38px]" onClick={() => toggleFilter(key)}><FaTrash /></button>
                            </div>,
                        )}
                    </div>
                    
                    <div className="flex items-center gap-4">
                        <label className="flex items-center gap-4 mb-0 flex-1">
                            <span>Report Title:</span>
                            <input
                                className="input input-bordered flex-1"
                                type="text"
                                value={reportName}
                                onChange={e => setReportName(e.target.value)} />
                        </label>
                        <div className="actions">
                            <button
                                className="btn btn-primary"
                                onClick={checkAndGenerate}>
                                Create Report
                            </button>
                            {/* @todo not available yet */}
                            {/* <button className="mr-3 btn btn-primary">
                                Create CSV Report
                            </button>
                            <button className="mr-3 btn btn-primary">
                                Create Excel Report
                            </button>
                            <button className="btn btn-primary">
                                Create PDF Report
                            </button> */}
                        </div>
                    </div>
                
                </div>
            
            </fieldset>
            
            <fieldset>
                <legend>Created Reports</legend>
                <div className="fieldset-body">
                    {loading && (
                        <CircleSpinner className="mx-auto mt-10" />
                    )}
                    {reports?.count < 1 ? (
                        <div className="todo reports list">
                            <p>No reports found</p>
                        </div>
                    ) : (
                        <CheckReportsTable
                            headers={headers}
                            orderField={orderField}
                            orderDirection={orderDirection}
                            onHeaderClick={onHeaderClick}
                            fetchCheckReports={fetchCheckReports}
                            reports={reports} />
                    )}
                </div>
            </fieldset>
            
            <MappingModal
                checkMappings={checkMappings}
                onFinishMapping={onFinishMapping}
                showModal={showMappingsModal}
                showHideModal={setShowMappingsModal} />
        
        </div>
        
    )
    
}

export default CheckReports
